import React, { useEffect, useMemo, useState } from 'react';
import { Input, InputNumber, Select, Space, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';

import { getVitalSignsSchema } from '../../../../../../api/healthcare/vitalSignsApi';
import { VitalSignField } from '../../../../../../types/healthcare/VitalSignTypes';

const { Text } = Typography;

interface Props {
  taskConfig: Record<string, any>;
  onChange: (payload: Record<string, any>, isValid: boolean) => void;
}

/**
 * Schema-driven — mirrors VitalsForm.tsx's field-rendering pattern (canonical
 * `/vital-signs/schema` keys, e.g. `bp_systolic`/`bp_diastolic`, never a hardcoded
 * field-label map). `bmi` is excluded from the editable set: it is auto-calculated
 * from `weight_kg`/`height_cm`, never a manually entered field.
 */
const RecordVitalSignsTaskForm: React.FC<Props> = ({ taskConfig, onChange }) => {
  const requiredFields: string[] = taskConfig.required_fields ?? [];

  const { data: schema = [] } = useQuery({
    queryKey: ['vitalSignsSchema'],
    queryFn: getVitalSignsSchema,
    staleTime: 1000 * 60 * 60,
  });

  const fields = useMemo(() => {
    const candidates = schema.filter((f) => f.editable && f.key !== 'bmi');
    if (requiredFields.length === 0) return candidates;
    const byKey = new Map(candidates.map((f) => [f.key, f]));
    return requiredFields
      .map((key) => byKey.get(key))
      .filter((f): f is VitalSignField => f !== undefined);
  }, [schema, requiredFields]);

  const [values, setValues] = useState<Record<string, number | string | null>>({});

  const bmi = (() => {
    const w = values['weight_kg'];
    const h = values['height_cm'];
    if (typeof w === 'number' && typeof h === 'number' && w && h > 0) {
      const hm = h / 100;
      return Math.round((w / (hm * hm)) * 10) / 10;
    }
    return null;
  })();

  useEffect(() => {
    const sanitised = Object.fromEntries(
      Object.entries(values).filter(([, v]) => v !== null && v !== undefined),
    );
    if (bmi !== null) sanitised['bmi'] = bmi;
    const isValid = requiredFields.every((f) => sanitised[f] !== undefined);
    onChange({ values: sanitised }, isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, bmi]);

  const fieldStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 140px',
    alignItems: 'center',
    gap: 8,
    padding: '3px 0',
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={4}>
      {fields.map((field) => {
        const isRequired = requiredFields.includes(field.key);
        const label = field.unit ? `${field.label} (${field.unit})` : field.label;
        const codedOptions = Object.entries(field.options || {}).map(([value, optionLabel]) => ({
          value,
          label: optionLabel,
        }));

        let control: React.ReactNode;
        if (field.type === 'coded') {
          control =
            codedOptions.length > 0 ? (
              <Select
                size="small"
                allowClear
                placeholder="Select"
                options={codedOptions}
                value={(values[field.key] as string) ?? undefined}
                onChange={(v) => setValues((prev) => ({ ...prev, [field.key]: v ?? null }))}
                style={{ width: '100%' }}
                data-testid={`record-vitals-${field.key}`}
              />
            ) : (
              <Input
                size="small"
                allowClear
                placeholder="Enter value"
                value={(values[field.key] as string) ?? ''}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, [field.key]: e.target.value || null }))
                }
                data-testid={`record-vitals-${field.key}`}
              />
            );
        } else {
          control = (
            <InputNumber
              size="small"
              value={(values[field.key] as number) ?? null}
              onChange={(v) => setValues((prev) => ({ ...prev, [field.key]: v }))}
              min={field.min ?? undefined}
              max={field.max ?? undefined}
              style={{ width: '100%' }}
              addonAfter={field.unit ? <span style={{ fontSize: 10 }}>{field.unit}</span> : undefined}
              data-testid={`record-vitals-${field.key}`}
            />
          );
        }

        return (
          <div key={field.key} style={fieldStyle}>
            <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>
              {label}
              {isRequired && <span style={{ color: 'var(--mg-error)', marginLeft: 2 }}>*</span>}
            </Text>
            {control}
          </div>
        );
      })}

      {bmi !== null && (
        <div style={fieldStyle}>
          <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>BMI (calculated)</Text>
          <InputNumber
            size="small"
            value={bmi}
            disabled
            style={{ width: '100%' }}
            addonAfter={<span style={{ fontSize: 10 }}>kg/m²</span>}
          />
        </div>
      )}
    </Space>
  );
};

export default RecordVitalSignsTaskForm;
